import { serviceFlagLabel, type Lead, type ServiceFlag } from '../../types/lead.js';
import { LEAD_STAGE_TABS, type LeadSplitFilters, type LeadStageCounts, type LeadStageTab, type ReachedBy } from '../../types/leadSplit.js';
import { speedToLeadSeconds } from './leadList.js';
import { hidesTest } from './testVisibility.js';

// THE SHARED LEADS SPLIT VIEW: the pure rules (Phase 2, Part B). One definition of every filter, every stage tab and every
// count, used by the server query and asserted by its tests. No clock reads, no I/O.
//
// ⚠️ The stage tabs are NOT mutually exclusive (a booked lead can also need your team). ⚠️ The COUNTS ignore the stage filter,
// so a tab shows what it would hold under the other filters; the LIST applies it.

const digitsOf = (s: string): string => s.replace(/\D/g, '');

/** Name, email or phone. A phone search needs at least three digits (fewer would match half the book). */
export function matchesSearch(lead: Pick<Lead, 'name' | 'phone'> & { email?: string | null }, raw: string): boolean {
  const q = raw.trim().toLowerCase();
  if (q === '') return true;
  if ((lead.name ?? '').toLowerCase().includes(q)) return true;
  if ((lead.email ?? '').toLowerCase().includes(q)) return true;
  const d = digitsOf(q);
  if (d.length < 3) return false;
  const phone = digitsOf(lead.phone);
  // A local number ("0433 334 444") is the international one ("+61 433 334 444") with a leading zero: match both ways of typing it.
  return phone.includes(d) || (d.startsWith('0') && d.length > 3 && phone.includes(d.slice(1)));
}

/** Who reached the lead: nobody yet, or the channel that did. */
export function reachedByOf(lead: Pick<Lead, 'contact'>): ReachedBy {
  return lead.contact.status === 'not_contacted' ? 'none' : (lead.contact.via ?? 'call');
}

/** "Outcomes awaiting confirmation": the AI set it and no person has confirmed it yet. */
export const awaitingConfirmation = (lead: Pick<Lead, 'outcome'>): boolean => lead.outcome.setBy === 'ai' && lead.outcome.code !== 'unresolved';

/**
 * THE ONE DEFINITION OF "NEEDS YOUR TEAM" (the Leads tab and the portal Dashboard both read it): a person is needed, or an AI-set outcome
 * still waits for a person to confirm it. ⚠️ Never restate this test elsewhere: two copies made the Dashboard say 4 where the tab said 39.
 */
export const needsYourTeam = (lead: Pick<Lead, 'needsPerson' | 'outcome'>): boolean => lead.needsPerson.on || awaitingConfirmation(lead);

/**
 * A lead that floats to the top of the whole list: possible distress was flagged AND it still needs a person. ⚠️ A person clearing
 * "needs a person" (they have dealt with it) returns it to its normal place; the stamped time stays as the record that it happened.
 */
export const isDistressFirst = (lead: Pick<Lead, 'needsPerson'> & { distressAt?: string | null }): boolean => Boolean(lead.distressAt) && lead.needsPerson?.on === true;

/** Decides which clubs are relay-only (leads only sent to the CRM). Absent = none are. */
export type RelayOnlyOf = (accountId: string) => boolean;

export function inStage(lead: Lead, stage: LeadStageTab, relayOnly?: RelayOnlyOf): boolean {
  switch (stage) {
    case 'all':
      return true;
    case 'needs_team':
      return !lead.historic && needsYourTeam(lead);
    case 'not_contacted':
      // ⚠️ A relay-only club's leads are never contacted by us, so they are not "not contacted". A HISTORIC lead predates any contact by us.
      return !lead.historic && lead.contact.status === 'not_contacted' && !(relayOnly?.(lead.accountId) ?? false);
    case 'in_conversation':
      return lead.contact.status === 'in_conversation';
    case 'booked':
      return lead.outcome.code === 'callback_booked' || lead.outcome.code === 'tour_booked';
    case 'joined':
      return lead.outcome.code === 'joined';
    case 'not_interested':
      return lead.outcome.code === 'not_interested';
  }
}

/**
 * Every filter EXCEPT the stage tab. Test leads are out unless `includeTest` (staff only: the portal schema has no such field).
 */
export function matchesSplitFilters(lead: Lead, f: LeadSplitFilters, testVisible?: ReadonlySet<string>): boolean {
  if (hidesTest(lead, lead.isTest, testVisible) && f.includeTest !== true) return false;
  if (f.q && !matchesSearch(lead, f.q)) return false;
  if (f.receivedFrom && lead.receivedAt < f.receivedFrom) return false;
  if (f.receivedTo && lead.receivedAt > f.receivedTo) return false;
  if (f.quality && f.quality.length > 0 && !f.quality.includes(lead.profile.quality)) return false;
  if (f.source && (lead.source ?? '') !== f.source) return false;
  if (f.reachedBy && reachedByOf(lead) !== f.reachedBy) return false;
  return true;
}

export function countStages(leads: readonly Lead[], relayOnly?: RelayOnlyOf): LeadStageCounts {
  const counts = Object.fromEntries(LEAD_STAGE_TABS.map((t) => [t, 0])) as LeadStageCounts;
  for (const l of leads) for (const t of LEAD_STAGE_TABS) if (inStage(l, t, relayOnly)) counts[t] += 1;
  return counts;
}

/** Up to two letters from a name ("Jo Smith" -> "JS"); "?" when there is none. */
export function initialsOf(name: string | null | undefined): string {
  const words = (name ?? '').replace(/^\[TEST\]\s*/i, '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0]!.charAt(0);
  const last = words.length > 1 ? words[words.length - 1]!.charAt(0) : '';
  return (first + last).toUpperCase();
}

/** 13 -> "0:13", 125 -> "2:05", 3725 -> "1:02:05". */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${two(m)}:${two(sec)}` : `${m}:${two(sec)}`;
}

/** The list row's channel-and-speed tag: "Call 0:13", "AI SMS 0:04". Null when nobody has responded. */
export function firstResponseLabel(lead: Pick<Lead, 'receivedAt' | 'firstResponseAt' | 'firstResponseVia'> & Partial<Pick<Lead, 'firstResponseEnquiryAt'>>): string | null {
  const secs = speedToLeadSeconds(lead);
  if (secs === null || !lead.firstResponseVia) return null;
  return `${lead.firstResponseVia === 'call' ? 'Call' : 'AI SMS'} ${formatClock(secs)}`;
}

/** A single line for the list: whitespace collapsed, cut at `max` with an ellipsis. */
export function oneLine(text: string, max = 90): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length <= max ? t : `${t.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

/** The list endpoint's query string: the ONE place the split-view filters become a URL (the server schema is strict). */
export function splitQueryString(filters: LeadSplitFilters, extra: { accountId?: string | null; cursor?: string | null } = {}): string {
  const q = new URLSearchParams();
  if (extra.accountId) q.set('accountId', extra.accountId);
  if (extra.cursor) q.set('cursor', extra.cursor);
  if (filters.q?.trim()) q.set('q', filters.q.trim());
  if (filters.receivedFrom) q.set('receivedFrom', filters.receivedFrom);
  if (filters.receivedTo) q.set('receivedTo', filters.receivedTo);
  if (filters.quality && filters.quality.length > 0) q.set('quality', filters.quality.join(','));
  if (filters.source) q.set('source', filters.source);
  if (filters.reachedBy) q.set('reachedBy', filters.reachedBy);
  if (filters.stage && filters.stage !== 'all') q.set('stage', filters.stage);
  if (filters.includeTest) q.set('includeTest', 'true');
  if (filters.brandId) q.set('brandId', filters.brandId);
  if (filters.groupId) q.set('groupId', filters.groupId);
  const s = q.toString();
  return s ? `?${s}` : '';
}

// ---------------------------------------------------------------------------
// presentation helpers (pure, so every sentence is asserted without a render)
// ---------------------------------------------------------------------------

/** "just now", "5 min ago", "3 h ago", "2 d ago", then the date. `nowMs` is an argument: no clock reads here. */
export function timeAgo(iso: string, nowMs: number): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const secs = Math.round((nowMs - t) / 1000);
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days} d ago`;
  return new Date(t).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** The service badges as the split view words them: the two SMS ones carry the bot's name, the call ones read plainly. */
export function splitServiceLabel(flag: ServiceFlag, assistantName: string | null | undefined): string {
  if (flag === 's2lCallMade') return 'Call made';
  if (flag === 's2lConnected') return 'Call connected';
  return serviceFlagLabel(flag, assistantName);
}

export const SPLIT_PERIODS = [
  { id: '7', label: 'Last 7 days', days: 7 },
  { id: '30', label: 'Last 30 days', days: 30 },
  { id: '90', label: 'Last 90 days', days: 90 },
  { id: 'all', label: 'All time', days: null },
  { id: 'custom', label: 'Custom range', days: null },
] as const;
export type SplitPeriodId = (typeof SPLIT_PERIODS)[number]['id'];

/** The window start for a preset period; empty for "all" and "custom" (custom carries its own dates). */
export const splitRangeFor = (id: SplitPeriodId, nowMs: number): { receivedFrom?: string } => {
  const days = SPLIT_PERIODS.find((p) => p.id === id)?.days ?? null;
  return days === null ? {} : { receivedFrom: new Date(nowMs - days * 86_400_000).toISOString() };
};

/** The sentence under the stage tabs when nothing matches, in the Kyck voice (sentence case, no exclamation marks). */
export function emptyListMessage(filtersActive: boolean, stage: LeadStageTab): string {
  if (filtersActive) return 'No leads match these filters. Clear a filter to see more.';
  switch (stage) {
    case 'needs_team':
      return 'Nothing needs your team right now.';
    case 'booked':
      return 'No bookings yet.';
    case 'joined':
      return 'Nobody has joined yet.';
    default:
      return 'No leads here yet. New enquiries appear as they arrive.';
  }
}
